/**
 * The nested-space projection gate (M2.5) — the pure decision of whether a
 * container node renders its child space as a projected preview at a given
 * effective render scale + depth. Lifted out of `SpaceRenderer` so it is a
 * single, unit-testable source of truth (the renderer stays a pure component).
 */
import { BAND_COMPACT_START } from "./lodBands.js";

/**
 * Nested-space render cutoff (perf): below this effective render scale the
 * recursive child DOM is skipped entirely. Pinned to the compact-band STRUCTURE
 * start (`BAND_COMPACT_START`) — the scale at which the body slot begins to
 * open — so the child DOM mounts exactly as the slot opens, ready just before
 * the reveal band (`--arch-band-compact-reveal`) fades it in: no pop-in, yet
 * nothing renders while the overlay is still fully transparent.
 *
 * (Was a hard-coded 0.4 that predated the band constants, so a whole sub-band
 * rendered an invisible nested subtree — M2.5 reclaimed it. On a 324-module
 * board mid-zoom that was up to ~24 invisible subtrees at once.) Below the
 * cutoff the body is collapsed anyway, so the node-count savings compound on
 * deep trees.
 */
export const INNER_RENDER_SCALE = BAND_COMPACT_START;

/** Deepest level rendered in a single frame — beyond this, rebasing will have
 *  already pushed the focal, so we never actually hit the limit in practice. */
export const MAX_RECURSION = 6;

/**
 * Should a node project its child space at this effective render `scale` and
 * `depth`? True only when it has a space, is not the context-layer focal `hole`
 * (which draws its own interior), is within `MAX_RECURSION`, and sits at or
 * above `INNER_RENDER_SCALE`. The scale term is the load-bearing perf decision:
 * it keeps invisible (pre-reveal) nested subtrees out of the DOM.
 */
export function projectsNestedSpace(
  scale: number,
  depth: number,
  hasSpace: boolean,
  hole: boolean,
): boolean {
  return hasSpace && !hole && depth < MAX_RECURSION && scale >= INNER_RENDER_SCALE;
}

/**
 * The floor/grid extents for a nested-space preview, in the INNER space's
 * coordinates: the whole slot rect mapped through the inverse of the preview's
 * fit-contain transform. The well behind a preview used to shrink-wrap the
 * content (the space frame), so every container's well came out a different
 * size — this makes the floor fill the entire region the widget dedicates to
 * the subspace instead. Content framing is untouched: only the floor + grid
 * read this rect.
 */
export function nestedFloorRect(
  inner: {
    readonly originX: number;
    readonly originY: number;
    readonly width: number;
    readonly height: number;
  },
  slotW: number,
  slotH: number,
): { x: number; y: number; w: number; h: number } {
  const fit = Math.min(slotW / inner.width, slotH / inner.height);
  const tx = (slotW - inner.width * fit) / 2;
  const ty = (slotH - inner.height * fit) / 2;
  return {
    x: inner.originX - tx / fit,
    y: inner.originY - ty / fit,
    w: slotW / fit,
    h: slotH / fit,
  };
}
